/**
 * `CouldNotVerify` — the fail-closed "membership could not be verified" terminal
 * screen (032 US2). Distinct from `NoMembershipBlock`: there, non-membership was
 * CONFIRMED (the person is authenticated but not a member); here the membership
 * source was UNREACHABLE or errored, so the outcome is indeterminate and we make
 * NO membership claim. The two screens differ in copy and actions — this one has
 * NO join link (joining presumes a settled non-membership, which was not
 * established) and instead offers a retry that restarts the sign-in flow.
 *
 * Standalone terminal ROUTE. Mounted at the public `/tunnistus-epaonnistui`
 * route with `chrome: 'bare'` so it renders with NO member shell behind it
 * (D-130, same posture as `NoMembershipBlock`). The member callback 303s the
 * browser here (without minting a cookie) on an `unreachable`/`error` verify
 * outcome, so the visitor is ANONYMOUS. The ROUTE is the gate, so the component
 * renders UNCONDITIONALLY — no session discriminator, no early `return null`.
 *
 * Actions:
 *   - "Yritä uudelleen" (PRIMARY) — restart the sign-in flow by a full-page nav
 *     to `/login` (the backend Telia-start, a SERVER route). The visitor never
 *     authenticated as a member, so retrying the flow is the corrective path.
 *   - "Kirjaudu ulos" — confirm application-session revocation, then replace
 *     the page with `/login`; failures show the shared retry screen. Broker
 *     single-sign-out remains unconfirmed.
 *
 * Layering / accessibility: identical to `NoMembershipBlock` — a
 * `fixed inset-0 z-50 bg-card` overlay (token-backed colour, scale-step z-index,
 * no raw hex / no arbitrary bracket, FR-013 / FR-047), `role="dialog"` +
 * `aria-modal="true"` named by its `<h1>` (via `aria-labelledby`), focus moved
 * to the heading on mount, and no member shell behind it (the bare route).
 */

import { type JSX, useEffect, useId, useRef } from 'react';

import { useTranslate } from '../i18n/useTranslate.js';
import { Button } from '../primitives/Button.js';
import { useSignOut } from '../session/useSignOut.js';

const overlayClasses = 'fixed inset-0 z-50 overflow-y-auto bg-card';
const centerClasses = 'flex min-h-full items-center justify-center px-4 py-8';
const panelClasses = 'w-full max-w-md text-center';
const headingClasses = 'm-0 text-xl font-bold text-foreground outline-none';
const bodyClasses = 'mt-3 text-sm leading-relaxed text-muted-foreground';
const actionsClasses = 'mt-6 flex flex-col items-center gap-3';

export function CouldNotVerify(): JSX.Element {
  const t = useTranslate();
  const signOut = useSignOut();
  const headingId = useId();
  const headingRef = useRef<HTMLHeadingElement>(null);

  // Move focus to the heading on mount so a keyboard / screen-reader user lands
  // on the dialog (its purpose) rather than the body. The component renders
  // unconditionally (the bare route is the gate), so this runs on every mount.
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <div role="dialog" aria-modal="true" aria-labelledby={headingId} className={overlayClasses}>
      <div className={centerClasses}>
        <div className={panelClasses}>
          <h1 ref={headingRef} tabIndex={-1} id={headingId} className={headingClasses}>
            {t('shell.couldNotVerify.heading')}
          </h1>
          <p className={bodyClasses}>{t('shell.couldNotVerify.body')}</p>
          <div className={actionsClasses}>
            <Button
              type="button"
              variant="primary"
              size="md"
              onClick={() => {
                // Restart the sign-in flow: a full-page nav to the backend
                // Telia-start (`/login`, a SERVER route). No broker sign-out —
                // the visitor never authenticated as a member.
                window.location.replace('/login');
              }}
            >
              {t('shell.couldNotVerify.retry')}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="md"
              onClick={() => {
                void signOut();
              }}
            >
              {t('shell.couldNotVerify.logout')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
